import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import {
  createPortal,
} from 'react-dom'


export interface GameDialogAction {
  label: string
  onClick: () => void
  primary?: boolean
  disabled?: boolean
}


export interface GameDialogLine {
  text: string
}


interface GameDialogProps {
  open: boolean
  avatar: string
  avatarFallbacks?: string[]
  speaker: string
  lines: GameDialogLine[]
  actions?: GameDialogAction[]
  typingSpeed?: number
  onClose?: () => void
  closeLabel?: string
  allowEscape?: boolean
}


export function GameDialog({
  open,
  avatar,
  avatarFallbacks = [],
  speaker,
  lines,
  actions = [],
  typingSpeed = 24,
  onClose,
  closeLabel = 'Закрыть',
  allowEscape = true,
}: GameDialogProps) {
  const [
    lineIndex,
    setLineIndex,
  ] = useState(0)

  const [
    visibleCharacters,
    setVisibleCharacters,
  ] = useState(0)

  const [
    reduceMotion,
    setReduceMotion,
  ] = useState(false)

  const [
    avatarIndex,
    setAvatarIndex,
  ] = useState(0)

  const avatarSources = useMemo(
    () =>
      [avatar, ...avatarFallbacks]
        .filter(Boolean)
        .filter(
          (source, index, items) =>
            items.indexOf(source) === index,
        ),
    [avatar, avatarFallbacks],
  )

  const currentAvatar =
    avatarSources[avatarIndex]
    ?? avatar

  const dialogRef =
    useRef<HTMLDivElement | null>(null)

  const currentLine =
    lines[lineIndex] ?? null

  const currentText =
    currentLine?.text ?? ''

  const isTyping =
    visibleCharacters < currentText.length

  const isLastLine =
    lineIndex >= lines.length - 1

  const showActions =
    isLastLine &&
    !isTyping &&
    actions.length > 0

  const displayedText =
    useMemo(
      () =>
        currentText.slice(
          0,
          visibleCharacters,
        ),
      [
        currentText,
        visibleCharacters,
      ],
    )


  useEffect(() => {
    const mediaQuery =
      window.matchMedia(
        '(prefers-reduced-motion: reduce)',
      )

    const updatePreference = () => {
      setReduceMotion(
        mediaQuery.matches,
      )
    }

    updatePreference()

    mediaQuery.addEventListener(
      'change',
      updatePreference,
    )

    return () => {
      mediaQuery.removeEventListener(
        'change',
        updatePreference,
      )
    }
  }, [])


  useEffect(() => {
    if (!open) {
      return
    }

    setLineIndex(0)
    setVisibleCharacters(0)
  }, [
    open,
    lines,
  ])


  useEffect(() => {
    setAvatarIndex(0)
  }, [
    avatar,
    avatarFallbacks,
  ])


  useEffect(() => {
    if (!open) {
      return
    }

    if (!currentText) {
      setVisibleCharacters(0)
      return
    }

    if (reduceMotion) {
      setVisibleCharacters(
        currentText.length,
      )

      return
    }

    setVisibleCharacters(0)
  }, [
    open,
    lineIndex,
    currentText,
    reduceMotion,
  ])


  useEffect(() => {
    if (
      !open ||
      !currentText ||
      reduceMotion ||
      visibleCharacters >=
        currentText.length
    ) {
      return
    }

    const timer =
      window.setTimeout(
        () => {
          setVisibleCharacters(
            (current) =>
              Math.min(
                current + 1,
                currentText.length,
              ),
          )
        },
        typingSpeed,
      )

    return () => {
      window.clearTimeout(
        timer,
      )
    }
  }, [
    open,
    currentText,
    visibleCharacters,
    typingSpeed,
    reduceMotion,
  ])


  useEffect(() => {
    if (!open) {
      return
    }

    const previousOverflow =
      document.body.style.overflow

    document.body.style.overflow =
      'hidden'

    return () => {
      document.body.style.overflow =
        previousOverflow
    }
  }, [
    open,
  ])


  const advance =
    useCallback(() => {
      if (!currentLine) {
        return
      }

      /*
       * Первый клик во время печати
       * мгновенно показывает всю
       * текущую реплику.
       */
      if (isTyping) {
        setVisibleCharacters(
          currentText.length,
        )

        return
      }

      /*
       * Если есть следующая реплика,
       * переходим к ней.
       */
      if (!isLastLine) {
        setLineIndex(
          (current) =>
            current + 1,
        )

        return
      }

      /*
       * На последней реплике с
       * действиями ждём выбора
       * пользователя.
       */
      if (actions.length > 0) {
        return
      }

      onClose?.()
    }, [
      currentLine,
      isTyping,
      currentText.length,
      isLastLine,
      actions.length,
      onClose,
    ])


  useEffect(() => {
    if (!open) {
      return
    }

    const handleKeyDown = (
      event: KeyboardEvent,
    ) => {
      if (
        event.key === 'Escape' &&
        allowEscape &&
        onClose
      ) {
        event.preventDefault()

        onClose()

        return
      }

      if (
        event.key === 'Enter' ||
        event.key === ' '
      ) {
        const target =
          event.target as HTMLElement | null

        /*
         * Не перехватываем Space/Enter,
         * если фокус находится на кнопке.
         */
        if (
          target?.closest(
            'button, a, input, textarea, select',
          )
        ) {
          return
        }

        event.preventDefault()

        advance()
      }
    }

    window.addEventListener(
      'keydown',
      handleKeyDown,
    )

    return () => {
      window.removeEventListener(
        'keydown',
        handleKeyDown,
      )
    }
  }, [
    open,
    allowEscape,
    onClose,
    advance,
  ])


  if (
    !open ||
    !currentLine
  ) {
    return null
  }


  const handleDialogClick = () => {
    if (showActions) {
      return
    }

    advance()
  }


  return createPortal(
    <div
      className="kr-game-dialog-backdrop"
      role="presentation"
    >
      <section
        ref={dialogRef}
        className="kr-game-dialog"
        role="dialog"
        aria-modal="true"
        aria-label={`Диалог: ${speaker}`}
      >
        <div
          className="kr-game-dialog__grid"
          aria-hidden="true"
        />

        <div
          className="kr-game-dialog__glow"
          aria-hidden="true"
        />

        {onClose && (
          <button
            type="button"
            className="kr-game-dialog__close"
            aria-label={closeLabel}
            onClick={(event) => {
              event.stopPropagation()
              onClose()
            }}
          >
            ×
          </button>
        )}

        <button
          type="button"
          className="kr-game-dialog__conversation"
          onClick={
            handleDialogClick
          }
          aria-label={
            isTyping
              ? 'Показать реплику полностью'
              : !isLastLine
                ? 'Продолжить диалог'
                : undefined
          }
        >
          <div className="kr-game-dialog__character">
            <div className="kr-game-dialog__portrait">
              <div
                className="kr-game-dialog__portrait-glow"
                aria-hidden="true"
              />

              <img
                src={currentAvatar}
                alt=""
                draggable={false}
                onError={() => {
                  setAvatarIndex((current) =>
                    Math.min(
                      current + 1,
                      Math.max(
                        avatarSources.length - 1,
                        0,
                      ),
                    ),
                  )
                }}
              />

              <span
                className="kr-game-dialog__portrait-corner kr-game-dialog__portrait-corner--tl"
                aria-hidden="true"
              />

              <span
                className="kr-game-dialog__portrait-corner kr-game-dialog__portrait-corner--br"
                aria-hidden="true"
              />
            </div>

            <div className="kr-game-dialog__speaker">
              {speaker}
            </div>
          </div>

          <div className="kr-game-dialog__content">
            <div className="kr-game-dialog__meta">
              <span className="kr-game-dialog__signal">
                <i />

                COMMS ONLINE
              </span>

              <span className="kr-game-dialog__counter">
                {String(
                  lineIndex + 1,
                ).padStart(
                  2,
                  '0',
                )}

                {' / '}

                {String(
                  lines.length,
                ).padStart(
                  2,
                  '0',
                )}
              </span>
            </div>

            <div className="kr-game-dialog__text">
              <span>
                {displayedText}
              </span>

              {isTyping && (
                <span
                  className="kr-game-dialog__cursor"
                  aria-hidden="true"
                />
              )}
            </div>

            <div className="kr-game-dialog__footer">
              {!isTyping &&
                !isLastLine && (
                  <span className="kr-game-dialog__continue">
                    Нажмите, чтобы продолжить

                    <i aria-hidden="true">
                      ▼
                    </i>
                  </span>
                )}

              {isTyping && (
                <span className="kr-game-dialog__skip">
                  Нажмите, чтобы пропустить
                </span>
              )}

              {!isTyping &&
                isLastLine &&
                actions.length === 0 && (
                  <span className="kr-game-dialog__continue">
                    Нажмите, чтобы закрыть

                    <i aria-hidden="true">
                      ▼
                    </i>
                  </span>
                )}
            </div>
          </div>
        </button>

        {showActions && (
          <div className="kr-game-dialog__actions">
            {actions.map(
              (
                action,
                index,
              ) => (
                <button
                  type="button"
                  key={`${action.label}-${index}`}
                  className={[
                    'kr-game-dialog__action',
                    action.primary
                      ? 'kr-game-dialog__action--primary'
                      : '',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  disabled={
                    action.disabled
                  }
                  onClick={
                    action.onClick
                  }
                >
                  <span>
                    {action.label}
                  </span>

                  {action.primary && (
                    <span
                      className="kr-game-dialog__action-arrow"
                      aria-hidden="true"
                    >
                      →
                    </span>
                  )}
                </button>
              ),
            )}
          </div>
        )}

        <div
          className="kr-game-dialog__scanline"
          aria-hidden="true"
        />
      </section>
    </div>,
    document.body,
  )
}
